import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  negativeReactions,
  positiveReactions,
  reactionMessage,
  reactionOf,
  type Reaction,
} from "../chat/reactions.ts";
import { defineMessages, getLocale, useT } from "../i18n/index.ts";

const messages = defineMessages({ reactions: "Préciser votre avis" }, { reactions: "Say more" });

/** Durée d'un appui long, et du survol à la souris avant d'ouvrir les emojis. */
const LONG_PRESS_MS = 450;
const HOVER_MS = 500;

export type Side = "up" | "down";

/**
 * Emojis qui précisent un pouce : un appui long, ou le survol à la souris, ouvre la rangée
 * au-dessus des pouces. `onPick` reçoit la note à envoyer : la même réaction choisie de
 * nouveau retire la note (null), comme dans l'app.
 */
export function useReactionPicker(
  rating: number | null,
  ratingMessage: string | null,
  onPick: (isGood: boolean | null, message: string | null) => void,
) {
  const t = useT(messages);
  const [open, setOpen] = useState<Side | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  const group = useRef<HTMLDivElement>(null);
  const reaction = reactionOf(rating, ratingMessage);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clearTimer, []);

  // Fermeture au clic ailleurs ou à Échap.
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!group.current?.contains(e.target as Node)) setOpen(null);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const later = (side: Side, ms: number, fromPress: boolean) => {
    clearTimer();
    timer.current = setTimeout(() => {
      if (fromPress) longPressed.current = true;
      setOpen(side);
    }, ms);
  };

  /** À appeler au début du clic d'un pouce : vrai si ce clic termine un appui long, à ignorer. */
  const endedLongPress = () => {
    clearTimer();
    const was = longPressed.current;
    longPressed.current = false;
    if (!was) setOpen(null);
    return was;
  };

  const groupProps = {
    ref: group,
    onPointerLeave: (e: PointerEvent) => {
      if (e.pointerType === "mouse") setOpen(null);
    },
  };

  const triggers = (side: Side) => ({
    "aria-haspopup": "true" as const,
    "aria-expanded": open === side,
    onPointerDown: (e: PointerEvent) => {
      longPressed.current = false;
      if (e.button === 0) later(side, LONG_PRESS_MS, true);
    },
    onPointerUp: clearTimer,
    onPointerCancel: clearTimer,
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType === "mouse" && !open) later(side, HOVER_MS, false);
    },
    onPointerLeave: clearTimer,
    // Sur mobile, l'appui long ouvrirait le menu du navigateur.
    onContextMenu: (e: MouseEvent) => e.preventDefault(),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(side);
      }
    },
  });

  const pick = (side: Side, r: Reaction) => {
    setOpen(null);
    const again = rating === (side === "up" ? 1 : 0) && reaction?.emoji === r.emoji;
    onPick(again ? null : side === "up", again ? null : reactionMessage(r));
  };

  const en = getLocale() === "en";
  const menu: JSX.Element | null = open ? (
    <div class={`msb-reactions msb-reactions--${open}`} role="menu" aria-label={t("reactions")}>
      {(open === "up" ? positiveReactions : negativeReactions).map((r) => {
        const label = en ? r.en : r.fr;
        return (
          <button
            key={r.emoji}
            type="button"
            role="menuitem"
            class="msb-reactions__item"
            aria-label={label}
            title={label}
            aria-current={reaction?.emoji === r.emoji && rating === (open === "up" ? 1 : 0)}
            onClick={() => pick(open, r)}
          >
            {r.emoji}
          </button>
        );
      })}
    </div>
  ) : null;

  return { reaction, groupProps, triggers, endedLongPress, menu };
}
